import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import {
  AbstractControlOptions,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CustomValidators } from '../../models/custom-validators';
import {
  DynamicFormEntry,
  DynamicFormEntryCell,
  DynamicFormEntryRow,
} from '../../models/dynamic-form-entry';

@Component({
  selector: 'bom-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit {
  @Input()
  config: DynamicFormEntry[] = [];

  @Input()
  form: FormGroup;

  @Input()
  rettighed: string;

  @Input()
  hentFraMinProfilButton: TemplateRef<any>;

  internalForm: FormGroup;

  firstAutoFillElementName: string;

  triggerAutoSave: boolean;

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit() {
    this.internalForm = this.form || this.formBuilder.group({});
    this.addControls();
    this.firstAutoFillElementName =
      this.config.filter(c => !!c.autofill).length > 0
        ? this.config.filter(c => !!c.autofill)[0].name
        : undefined;
    if (this.triggerAutoSave) {
      this.internalForm.markAsDirty();
    }
  }

  private addControls() {
    if (this.config) {
      this.config.forEach(entry => {
        if (entry.rows) {
          this.internalForm.addControl(entry.name, this.buildRowsGroup(entry));
        } else {
          const validators = this.buildValidators(entry);
          if (entry.type === 'checkbox') {
            // null equates to the checkbox being unchecked, upon accessing the view
            // When the user unchecks, the value is False upon unchecking
            if (entry.value !== null) {
              if (entry.value === undefined || entry.value.toLowerCase() === 'false') {
                entry.value = null;
              } else {
                entry.value = 'True';
              }
            }
          }
          if (entry.defaultValue && !entry.value) {
            entry.value = entry.defaultValue ?? undefined;
            this.triggerAutoSave = true;
          }
          this.internalForm.addControl(
            entry.name,
            this.formBuilder.control(entry.value, {
              validators: validators,
              updateOn: entry.updateOn,
            } as any)
          );
        }
      });
    }
  }

  private buildValidators(entry: DynamicFormEntry): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (entry.minLength) {
      validators.push(Validators.minLength(entry.minLength));
    }

    if (entry.maxLength) {
      validators.push(Validators.maxLength(entry.maxLength));
    }

    if (entry.validation) {
      validators.push(Validators.pattern(entry.validation));
    }

    if (entry.required) {
      validators.push(CustomValidators.trimRequired());
      validators.push(Validators.required);
    }

    if (entry.min) {
      validators.push(CustomValidators.danishNumberMin(entry.min));
    }

    if (entry.max) {
      validators.push(CustomValidators.danishNumberMax(entry.max));
    }

    return validators;
  }

  private buildValidatorsForRow(entry: DynamicFormEntryCell): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (entry.inputType === 'long') {
      validators.push(Validators.pattern('^-?[0-9]*$'));
    }

    if (entry.inputType === 'string') {
      validators.push(Validators.maxLength(200));
    }

    return validators;
  }

  private buildRowsGroup(entry: DynamicFormEntry): FormGroup {
    const controls: { [key: string]: any } = {};
    entry.rows.forEach(row => {
      if (this.rettighed === 'Laes') {
        controls[row.id].disable({ emitEvent: false });
      }
      controls[row.id] = this.buildCellsGroup(row);
    });
    const updateOptions: AbstractControlOptions = { updateOn: entry.updateOn };
    return this.formBuilder.group(controls, updateOptions);
  }

  private buildCellsGroup(row: DynamicFormEntryRow): FormGroup {
    const controls: { [key: string]: any } = {};
    row.cells.forEach(
      cell =>
        // Denne tilføjer hver enkelt cell.value og validators til hver enkelt cell, hvis cell.inputType ikke er empty
        // Se her hvis der skal sættes validators på tabeller
        (controls[cell.columnId] = new FormControl(cell.value, this.buildValidatorsForRow(cell)))
    );
    row.cells.filter(cell => !cell.placeHolder).forEach(cell => (cell.placeHolder = ''));
    return this.formBuilder.group(controls);
  }
}
